<template>
  <div
    class="collectible-card-small position-relative py-2 d-inline-flex flex-column align-items-center"
    :style="{
      background: accentStyle.background,
      backgroundColor: accentStyle.backgroundColor,
      color: accentStyle.color,
    }"
  >
    <ion-checkbox
      v-if="isSelectable"
      class="select position-absolute mt-2 ml-n1"
      mode="ios"
      :checked="isSelected"
      @ionChange="select"
    />
    <div class="d-flex flex-column align-items-center clickable" @click="toggleSelection">
      <div class="flex-grow-1 top-section" :style="{ backgroundColor: accentStyle.backgroundColor }">
        <p class="name">{{ truncate(collectible.name, 14) }}</p>
      </div>
      <div class="flex-grow-1 mt-1 mb-3">
        <span class="left-desc">{{ collectible.item_type }}</span>
        <span class="right-desc">{{ collectible.rarity }}</span>
      </div>

      <img
        v-image
        class="collectible-image"
        :src="get(collectible, 'image', '/empty.png')"
        @click="openCollectibleCardModal"
      />
      <div class="flex-grow-1 desc g-0" :style="{ backgroundColor: accentStyle.backgroundColor }">
        <span class="d-block bold source">{{ collectible.source || 'Source' }}</span>
        <span
          v-if="collectible.count >= 10"
          class="item-count m-0"
          :style="{ backgroundColor: accentStyle.backgroundColor }"
        >
          {{ collectible.count }}
        </span>
        <span v-else>{{ collectible.short_description }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Emit, Prop, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { Collectible, User } from '@/types/static-types';
import { truncate } from '@/utils/string';
import namespace from '@/store/namespace';

import { toast } from '@/native';
import { authStore } from '~/pinia-store/auth';

@Options({
  name: 'FeaturedCollectibleCard',
  components: {},
})
export default class FeaturedCollectibleCard extends Vue {
  @Prop({ default: false }) public disabled!: boolean;
  @Prop({ default: false }) public isSelectable!: boolean;
  @Prop({ default: false }) public isSelected!: boolean;
  @Prop({ default: () => ({}) }) collectible!: Collectible;
  @Prop({ default: 0 }) selectedCount!: number;

  public accentStyle: any = {};
  truncate = truncate;

  public toggleSelection() {
    if (!this.isSelected && this.selectedCount >= 5) {
      toast.show('You can only feature up to 5 collectibles.', 'nonative', 'danger');
    } else {
      const data = {
        detail: {
          checked: !this.isSelected,
        },
      } as CustomEvent;
      this.select(data);
    }
  }

  public get user() {
    const { user } = authStore();
    return user.value;
  }

  get = get;

  @Emit()
  public select(e: CustomEvent) {
    return e.detail.checked;
  }

  public recalcColors(collectible: Collectible | null) {
    if (!collectible) return null;
    if (collectible.rarity === 'Amethyst Rare') {
      this.accentStyle = {
        color: 'white',
        background: 'linear-gradient(180deg, rgba(174, 56, 229, 0) -75%, #AE00FF 100%)',
        backgroundColor: 'transparent',
      };
    } else if (collectible.rarity === 'Ultra Rare') {
      this.accentStyle = {
        color: '#FFA500',
        backgroundColor: '#FFA500',
      };
    } else if (collectible.rarity === 'Super Rare') {
      this.accentStyle = {
        color: '#00B4C5',
        backgroundColor: '#00B4C5',
      };
    } else if (collectible.rarity === 'Rare') {
      this.accentStyle = {
        color: '#328FD2',
        backgroundColor: '#328FD2',
      };
    } else {
      this.accentStyle = {};
    }
  }

  @Watch('collectible')
  public onCollectibleChange(newCollectible: Collectible | null) {
    this.recalcColors(newCollectible);
  }

  public mounted() {
    this.recalcColors(this.collectible);
  }
}
</script>

<style lang="sass" scoped>
.select
  left: 1rem
.disabled
  pointer-events: none
  *
    pointer-events: none
.actions
  transform: scale(0.8)
  max-width: 200px
  .inline-button
    &:not(:first-of-type)
      margin: 0 5px
.clickable
  cursor: pointer
.clickable:hover
  opacity: 0.7
.collectible-card-small
  background-color: #fff
  border-radius: 12px
  padding: 0 !important
  width: 160px
  height: 200px !important
  min-width: 160px
  *
    font-size: 14px
  img
    width: 150px
    height: 100px
    object-fit: contain
    user-select: none !important
  .name
    margin: 0 0 5px 0
    font-size: 16px
    font-weight: bold
  .left-desc
    position: absolute
    font-size: 11px
    left: 10px
    font-weight: bold
  .top-section
    width: 160px
    height: 30px
    text-align: center
    background-color: #340A6D
    border-radius: 12px 12px 0 0
    color: white
    line-height: 30px
  .desc
    display: block
    color: white
    background-color: #340A6D
    text-align: center
    height: 50px
    width: 160px
    margin: 0
    padding-top: 4px
    border-radius: 0 0 12px 12px
    line-height: 12px
    font-size: 11px
    span
      margin: 4px
      font-size: 11px
    .source
      margin: 0 0 3px 0
  .right-desc
    position: absolute
    font-size: 11px
    right: 10px
  .icon-button
    color: #ae38e5
    font-size: 20px
    .d-flex
      font-size: 20px
.primary-color
  color: #7f10b3
.secondary-color
  color: #3dc2ff
.routerlink
  color: #214163
.item-count
  display: inline-block
  background-color: #CD67EF
  border-radius: 5px
  line-height: 16px
  font-size: 12px
  padding: 3px 15px
</style>
